import { AlertsInsights } from "@bcc/components/dashboard/alerts-insights";
import { DashboardStats } from "@bcc/components/dashboard/dashboard-stats";
import { OrdersByProductChart } from "@bcc/components/dashboard/orders-by-product-chart";
import { SalesTrendChart } from "@bcc/components/dashboard/sales-trend-chart";
import { Button } from "@bcc/components/ui/button";
import { useAuth } from "@bcc/contexts/auth-context";
import { Download } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  setDashboardViewChartGridTestId,
  setDashboardViewExportButtonTestId,
  setDashboardViewGreetingTestId,
  setDashboardViewLastUpdatedTestId,
  setDashboardViewSubtitleTestId,
  setDashboardViewTestId,
} from "./dashboard-view.utils";

interface AlertData {
  id: number;
  type: "error" | "warning" | "success" | "info";
  icon: string;
  title: string;
  message: string;
  primaryAction: string;
  primaryLink: string;
  secondaryAction: string;
  bgColor: string;
  iconColor: string;
  primaryButtonColor: string;
}

interface SlaImpact {
  score: number;
  impact: number;
  status: "excellent" | "fair" | "risk";
  message: string;
}

interface StatData {
  name: string;
  baseValue: number;
  value: string;
  change: string;
  changeType: "positive" | "negative" | "neutral";
  description: string;
  icon: string;
  sparklineData: Array<{ day: number; value: number }>;
  color: string;
  bgColor: string;
  sla: SlaImpact;
}

interface SalesTrendDataPoint {
  date: string;
  sales: number;
}

interface SalesTrendData {
  sevenDay: SalesTrendDataPoint[];
  thirtyDay: SalesTrendDataPoint[];
}

interface OrdersByProductDataPoint {
  product: string;
  orders: number;
  color: string;
}

interface DashboardViewProps {
  alertsData: AlertData[];
  statsData: StatData[];
  salesTrendData: SalesTrendData;
  ordersByProductData: OrdersByProductDataPoint[];
  testId?: string;
}

export function DashboardView({ alertsData, statsData, salesTrendData, ordersByProductData, testId }: DashboardViewProps) {
  const { roaster } = useAuth();
  const { t } = useTranslation();
  const [lastUpdated] = useState(new Date());

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return t("views.dashboard.greetings.morning");
    if (hour < 18) return t("views.dashboard.greetings.afternoon");
    return t("views.dashboard.greetings.evening");
  };

  const handleExportReport = () => {
    const data = {
      generatedAt: new Date().toISOString(),
      metrics: {
        totalRevenue: "$48,574",
        totalOrders: "1,847",
        activeProducts: "127",
        labelsPrinted: "3,492",
        slaScore: 92,
        slaStatus: "Excellent",
      },
      slaBreakdown: {
        fulfillmentSpeed: 96,
        orderAccuracy: 99,
        responseRate: 93,
        onTimeDispatch: 95,
      },
      slaImpact: {
        salesImpact: 0.95,
        ordersImpact: 0.87,
        productsImpact: 0.92,
        payoutImpact: 1.01,
        notes: {
          sales: "SLA fulfillment dropped to 87%, lowering daily conversions by 5%.",
          orders: "3 orders delayed beyond SLA target.",
          products: "2 SKUs paused due to SLA risk.",
          payout: "Payout adjusted by SLA performance (+1.0%).",
        },
      },
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `dashboard-report-${new Date().toISOString().split("T")[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid={testId ?? setDashboardViewTestId("container")} className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-balance" data-testid={setDashboardViewGreetingTestId()}>
            {getGreeting()}, {roaster?.roasterName || t("views.dashboard.defaultPartner")}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground" data-testid={setDashboardViewSubtitleTestId()}>
            {t("views.dashboard.subtitle")}
          </p>
        </div>
        <Button
          onClick={handleExportReport}
          variant="outline"
          className="gap-2 bg-transparent"
          data-testid={setDashboardViewExportButtonTestId()}
        >
          <Download className="h-4 w-4" />
          {t("views.dashboard.exportReport")}
        </Button>
      </div>

      <AlertsInsights data={alertsData} />

      <DashboardStats data={statsData} />

      <div className="grid gap-6 lg:grid-cols-12" data-testid={setDashboardViewChartGridTestId()}>
        <div className="lg:col-span-8">
          <SalesTrendChart data={salesTrendData} />
        </div>
        <div className="lg:col-span-4">
          <OrdersByProductChart data={ordersByProductData} />
        </div>
      </div>

      <div className="flex justify-end">
        <p className="text-xs text-muted-foreground" data-testid={setDashboardViewLastUpdatedTestId()}>
          {t("views.dashboard.lastUpdated")}{" "}
          {lastUpdated.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
          })}
        </p>
      </div>
    </div>
  );
}
